import { useContext, useEffect, useState } from 'react';
import { DataError, DataState, DataStatus } from '../types/appTypes';
import { UserContext } from '../context/userContext';

export type UseDataArgs<T, RT> = {
  endpoint: string;
  method?: string;
  initialData?: RT;
  initialStatus?: DataStatus;
  loadWhen?: boolean;
  appendWhen?: boolean;
  rawResponse?: boolean;
  modifyData?: (data: T | undefined) => RT | undefined;
  appendData?: (
    oldData: RT | undefined,
    newData: RT | undefined
  ) => RT | undefined;
  onBegin?: () => void;
  onComplete?: (
    data: RT | undefined,
    status: DataStatus,
    error?: DataError
  ) => void;
  body?: BodyInit | null;
};

export function useData<T, RT = T>({
  endpoint,
  method = 'GET',
  initialData = undefined,
  initialStatus = DataStatus.Idle,
  loadWhen = undefined,
  appendWhen = false,
  rawResponse = false,
  modifyData = undefined,
  appendData = undefined,
  onBegin = undefined,
  onComplete = undefined,
  body = undefined,
}: UseDataArgs<T, RT>): { dataState: DataState<RT>; reloadData: () => void } {
  const { user } = useContext(UserContext);
  const [reloadToggle, setReloadToggle] = useState(false);
  const [dataState, setDataState] = useState<DataState<RT>>({
    status: initialStatus,
    error: null,
    data: initialData,
  });

  const reloadData = () => {
    setReloadToggle((pv) => !pv);
  };

  const handleOnComplete = (
    data: RT | undefined,
    status: DataStatus,
    error?: DataError
  ) => {
    if (onComplete !== undefined) onComplete(data, status, error);
  };

  useEffect(() => {
    if (loadWhen !== undefined && !loadWhen) return;

    if (onBegin !== undefined) onBegin();

    setDataState((pv) => {
      return { ...pv, status: DataStatus.Loading };
    });

    const headers = { 'Content-Type': 'application/json' };
    fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
      method: method,
      headers:
        user === undefined || user === null
          ? headers
          : {
              ...headers,
              Authorization: 'Bearer ' + user!.accessToken,
            },
      body: body,
    })
      .then(async (response) => {
        let data = !rawResponse ? await response.json() : await response.text();
        if (response.status === 200) {
          if (modifyData !== undefined) {
            data = modifyData(data);
          }

          setDataState((pv) => {
            let retData: RT | undefined;
            if (appendWhen && appendData !== undefined) {
              retData = appendData(pv.data, data);
            } else {
              retData = data;
            }

            return {
              status: DataStatus.Ready,
              error: null,
              data: retData,
            };
          });
          handleOnComplete(data, DataStatus.Ready);
        } else if (response.status === 404) {
          setDataState((pv) => {
            return { ...pv, status: DataStatus.NotFound, error: data };
          });
          handleOnComplete(undefined, DataStatus.NotFound, data);
        } else {
          setDataState((pv) => {
            return { ...pv, status: DataStatus.Fail, error: data };
          });
          handleOnComplete(undefined, DataStatus.Fail, data);
        }
      })
      .catch((err) => {
        setDataState((pv) => {
          return { ...pv, status: DataStatus.Error, error: null };
        });
        handleOnComplete(undefined, DataStatus.Error);
      });
  }, [user, loadWhen, reloadToggle]);

  return { dataState, reloadData };
}
